import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect } from 'react';
import { AVAILABLE_COMBO_STOCK, IS_PREBOOK_FLOW, OUT_OF_STOCK_COMBOS } from '@/constants/common';
import { CartContext } from '@/context/CartProvider';
import { ProductOutOfStock } from '@/models/Cart';
import { ComboVariant2 } from '@/models/Combo';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import { getLocalStorageKey, isSessionStorageAvailable } from '@/utils/helper';
import Caption, { VARIANT as CAPTION_VARIANT } from '../Caption';
import Paragraph, { VARIANT as PARAGRAPH_VARIANT } from '../Paragraph';
import ProductImage from '../products/ProductImage';
import ProductPrice from '../products/ProductPrice';
import ComboAddToCart from './ComboAddToCart';
interface Props {
  variant: ComboVariant2;
  position: number;
  availableQty?: number;
  comboSlug: string;
  handleOutOfProduct: (item: ProductOutOfStock) => void;
}
const ComboCard = ({
  variant,
  position,
  availableQty,
  comboSlug,
  handleOutOfProduct
}: Props) => {
  const {
    t
  } = useTranslation('products');
  const {
    comboItems,
    updateCart
  } = useContext(CartContext);
  const actualPrice = Number(variant.price);
  const discountedPrice = actualPrice - Number(variant.discount_amount || 0);
  const quantity = comboItems[variant.id] || 0;
  const isProductOutOfStock = availableQty === 0;
  const StockInSS = typeof window !== 'undefined' && sessionStorage?.getItem(AVAILABLE_COMBO_STOCK);
  const availableStock = StockInSS ? JSON.parse(StockInSS) : undefined;
  useEffect(() => {
    if (isProductOutOfStock) {
      if (isSessionStorageAvailable(window)) {
        const items = sessionStorage.getItem(OUT_OF_STOCK_COMBOS);
        const outOfStockItems: ProductOutOfStock[] = items ? JSON.parse(items) : [];
        if (!outOfStockItems.some(item => item.id == variant.id.toString())) {
          const item: ProductOutOfStock = {
            id: variant.id.toString(),
            productName: variant.name,
            productImage: variant.image,
            brandName: '',
            quantity: variant.applicable_acres ? `${variant.applicable_acres} Acres` : ''
          };
          outOfStockItems.push(item);
          handleOutOfProduct(item);
        }
        sessionStorage.setItem(OUT_OF_STOCK_COMBOS, JSON.stringify(outOfStockItems));
      }
      updateCart({
        variantId: variant.id.toString(),
        quantity: quantity,
        variantPrice: variant.price,
        variantDiscount: variant.discount_amount,
        catalogueId: 0,
        groupId: {},
        isCombo: true,
        parentType: 'COMBO'
      }, false);
    }
    if (!!availableQty && quantity > availableQty) {
      updateCart({
        variantId: variant.id.toString(),
        quantity: quantity - availableQty,
        variantPrice: variant.price,
        variantDiscount: variant.discount_amount,
        catalogueId: 0,
        groupId: {},
        isCombo: true,
        parentType: 'COMBO'
      }, false);
    }
  }, [availableQty, handleOutOfProduct, isProductOutOfStock, quantity, updateCart, variant]);
  return !isProductOutOfStock ? <section className="mx-4 py-5 border border-transparent border-b-neutral-10 last:border-b-transparent" data-sentry-component="ComboCard" data-sentry-source-file="ComboCard.tsx">
      <Link className="flex items-center" href={`/product/combo/${comboSlug}${getLocalStorageKey(IS_PREBOOK_FLOW) ? '?isprebook=true' : ''}`} data-sentry-element="Link" data-sentry-source-file="ComboCard.tsx">
        <div className="flex flex-col items-center grow-0 shrink-0">
          <ProductImage imageUrl={variant.image} productName={variant.name} data-sentry-element="ProductImage" data-sentry-source-file="ComboCard.tsx" />
        </div>

        <div className="flex-1 ml-4">
          <Caption variant={CAPTION_VARIANT.ONE} className="font-nato-medium text-neutral-50" label={variant.parent_type} data-sentry-element="Caption" data-sentry-source-file="ComboCard.tsx" />

          <Paragraph variant={PARAGRAPH_VARIANT.BIG} isHighlighted className="text-neutral-90" data-sentry-element="Paragraph" data-sentry-source-file="ComboCard.tsx">
            {variant.name}
          </Paragraph>

          <div className="flex items-end">
            <div className="flex-1">
              {variant.applicable_acres ? <Paragraph variant={PARAGRAPH_VARIANT.BIG} isHighlighted className="text-neutral-90" testId="product-variant-name">
                  {variant.applicable_acres} Acres
                </Paragraph> : null}

              {discountedPrice > 0 ? <ProductPrice actualPrice={actualPrice} discountedPrice={discountedPrice} /> : null}
            </div>

            <div className="h-10 grow-0 shrink-0">
              <ComboAddToCart productName={`${variant?.parent_type} - ${variant?.name} - ${variant.applicable_acres}`} productVariant={variant} index={position} listType={ProductListTypeEnum.CART} productId={variant.id} data-sentry-element="ComboAddToCart" data-sentry-source-file="ComboCard.tsx" />
            </div>
          </div>
        </div>
      </Link>
      {availableStock?.[variant?.id] > 0 && <div className="bg-error-10 mt-4 px-4 py-3 rounded-lg text-error-100 text-center">
          {availableStock[variant.id] === 1 ? t('only_one_item_left') : t('only_few_items_left', {
        availableQty: availableStock[variant.id]
      })}
        </div>}
    </section> : null;
};
export default ComboCard;